 
import deLanguage from '@/translation/de.json'
import enLanguage from '@/translation/en.json'
import nlLanguage from '@/translation/nl.json'

import { isRecord } from './isRecord'
import { createLocalStorageItem } from './localStorageItem'

export const localStorageLanguageKey = 'employee-app-language'

export enum Locale {
    nl = 'nl',
    en = 'en',
    de = 'de',
}

const isLocale = (value: string): value is Locale => Object.values<string>(Locale).includes(value)

export const languageStorage = createLocalStorageItem<Locale | null>({
    key: localStorageLanguageKey,
    fallback: null,
    parse: raw => (isLocale(raw) ? raw : null),
})

export interface Language {
    code: Locale
    symbol: string
}

export const languages: Language[] = [
    { code: Locale.nl, symbol: '🇳🇱' },
    { code: Locale.de, symbol: '🇩🇪' },
    { code: Locale.en, symbol: '🇬🇧' },
]

const locales: Record<Locale, unknown> = {
    [Locale.nl]: nlLanguage,
    [Locale.en]: enLanguage,
    [Locale.de]: deLanguage,
}

export const getLocale = (): Locale => {
    if (typeof window === 'undefined') {
        return Locale.nl
    }

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const locationLanguage = urlParams.get('language')
    const language = locationLanguage || languageStorage.get()

    if (language === Locale.de) {
        return Locale.de
    }

    if (language === Locale.en) {
        return Locale.en
    }

    return Locale.nl
}

export type TransParams = Record<string, string | number | null | undefined>

// Resolves a translation like `common.save` and replaces its `:placeholder:`s with the given params (empty when missing)
export const trans = (path: string, params?: TransParams): string => {
    const selectors = path.split('.')
    const paramKeys = Object.keys(params || {})

    const locale = getLocale()

    let translation = locales[locale]

    for (let i = 0; i < selectors.length; i += 1) {
        const nextStep = isRecord(translation) ? translation[selectors[i]] : undefined

        if (! nextStep) {
            console.error(`No translation found for: ${path}`)
            return path
        }

        translation = nextStep
    }

    if (typeof translation !== 'string') {
        console.error(`No translation found for: ${path}`)
        return path
    }

    if (! params) {
        return translation
    }

    let result = translation

    for (let i = 0; i < paramKeys.length; i += 1) {
        const regex = new RegExp(`:${paramKeys[i]}:`, 'g')

        result = result.replace(regex, String(params[paramKeys[i]] ?? ''))
    }

    return result
}

export const getLanguage = (): Language => {
    const locale = getLocale()

    return languages.find(language => language.code === locale) || languages[0]
}

export const setLanguage = (locale: Locale) => {
    languageStorage.set(locale)
}
